import {
  useDownloadInvoiceReportSampleCsv,
  SampleCsvStatus,
  GetInvoiceReportSampleImportCsvParams,
} from '@/context/services/invoice_reports/InvoiceReportSampleCsv.service';
import { useCheckInvoiceReportPermission } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportPermission';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useDownload } from '@/hooks/useDownload';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';

export const useDownloadSampleCsv = () => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const download = useDownload();
  const fetchSampleCsv = useDownloadInvoiceReportSampleCsv();
  const checkPermission = useCheckInvoiceReportPermission();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();

  return useCallback(
    async (params: GetInvoiceReportSampleImportCsvParams) => {
      const genericErrorTitle = t(
        'invoice_reports.list_import_drawer.import_error_common_toast'
      );
      try {
        const result = await fetchSampleCsv(params);

        if (result.status === SampleCsvStatus.SUCCESS) {
          download({ value: result.blob, fileName: result.fileName });
          return;
        }

        if (result.status === SampleCsvStatus.UNAUTHORIZED) {
          location.reload();
          return;
        }

        if (
          result.status === SampleCsvStatus.FORBIDDEN ||
          result.status === SampleCsvStatus.PAYMENT_REQUIRED ||
          result.status === SampleCsvStatus.UNPROCESSABLE
        ) {
          const apiMsg = result.message || genericErrorTitle;
          await handleCheckPermission({
            fnCheckPermission: checkPermission,
            onCheckSuccess: () => {
              notify.error({ title: toHtmlNode(apiMsg), duration: 5 });
            },
            onCheckFailure: () => {
              location.reload();
            },
          });
          return;
        }

        notify.error({ title: genericErrorTitle, duration: 5 });
      } catch {
        notify.error({ title: genericErrorTitle, duration: 5 });
      }
    },
    [
      checkPermission,
      download,
      fetchSampleCsv,
      handleCheckPermission,
      notify,
      t,
    ]
  );
};
